import { useTranslation } from 'react-i18next';

import { changeLocale } from '../i18n';
import { normaliseLocale, SUPPORTED_LOCALES, type SupportedLocale } from '../i18n/direction';

/**
 * Switches between Arabic and English.
 *
 * Deliberately a plain, always-visible control rather than a setting buried in a profile menu:
 * clinics run mixed teams, a shared reception machine changes hands during a shift, and the
 * switch also mirrors the entire layout — so it needs to be one click from anywhere.
 *
 * Drawn as the capsule from `d1-login.html`: the language you are reading is filled in plum, the
 * other is quiet. That is worth stating because the obvious implementation gets it backwards —
 * disabling the current language leaves it looking faded and the *unselected* one looking chosen,
 * which is precisely the wrong signal on a control whose whole job is to tell you where you are.
 * The current option is still inert; it is styled as selected rather than as unavailable.
 *
 * Each label is written in the language it names — العربية in Arabic, English in English — because
 * someone who cannot read the current interface has to be able to find the way out of it.
 */
export function LanguageToggle() {
  const { t, i18n } = useTranslation();
  const current = normaliseLocale(i18n.language);

  return (
    <div className="lang-toggle" role="group" aria-label={t('common.language')}>
      {SUPPORTED_LOCALES.map((locale: SupportedLocale) => {
        const selected = locale === current;
        return (
          <button
            key={locale}
            type="button"
            className={selected ? 'lang-toggle__option is-selected' : 'lang-toggle__option'}
            lang={locale}
            aria-pressed={selected}
            // Inert rather than disabled: a disabled control is removed from the tab order, and a
            // keyboard user landing on this group should still be able to read both options.
            onClick={selected ? undefined : () => void changeLocale(locale)}
          >
            {locale === 'ar' ? t('common.arabic') : t('common.english')}
          </button>
        );
      })}
    </div>
  );
}
